// Offline-tolerant reads for the module-06 floor screens.
//
// A floor screen has three honest states and this hook computes all three:
//   LIVE     — a fresh online response.
//   CACHED   — the network is gone (or failed) and the last online snapshot is
//              being shown, WITH its age. The screen must say so; a stale
//              worksheet rendered as current is how a counter records against
//              yesterday's bin.
//   NOTHING  — offline and never loaded. An empty state that says "connect once
//              to load this", never a blank list that reads as "no lines".
//
// It wraps TanStack Query rather than replacing it so the online path keeps the
// area's retry/invalidations behaviour, and writes each successful payload into
// `floor-read-cache` verbatim (see that file on why "verbatim" is a blind-count
// requirement, not an optimisation).

import { useEffect, useMemo } from "react";
import { useQuery, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { loadRead, saveRead } from "../lib/floor-read-cache";
import { useOnline } from "./use-online";

export type FloorReadSource = "live" | "cached" | "none";

export interface FloorRead<T> {
  readonly data: T | undefined;
  readonly source: FloorReadSource;
  /** Epoch ms of the snapshot when `source === "cached"`. */
  readonly cachedAt: number | undefined;
  readonly isLoading: boolean;
  readonly error: unknown;
  readonly online: boolean;
  /**
   * A snapshot is on screen AND a live read is still in flight. The screen says
   * "checking for updates" rather than pretending the snapshot is current or
   * hiding it behind a skeleton.
   */
  readonly revalidating: boolean;
  refetch: () => void;
}

/** How long a floor read waits before it is treated as a black hole. */
export const FLOOR_READ_TIMEOUT_MS = 8_000;

/**
 * An abort signal for a floor read: the caller's signal (react-query's — the
 * one invalidate/cancel abort) combined with the black-hole deadline. A plant
 * AP with no backhaul swallows the connection rather than refusing it, so
 * without the deadline the request never settles; and without the CALLER's
 * signal a refresh-after-applied invalidate cannot cancel a stale in-flight
 * read — react-query then issues NO refetch and the pre-tap snapshot parks on
 * screen as "fresh" (the M04-S10 Start race, staging 2026-08-05; pinned in
 * use-cached-read.race.test.tsx). If combining is unsupported the caller's
 * signal wins: correctness over the timeout.
 */
export function floorReadSignal(
  base?: AbortSignal,
  ms: number = FLOOR_READ_TIMEOUT_MS,
): AbortSignal | null {
  try {
    const deadline = AbortSignal.timeout(ms);
    return base !== undefined ? AbortSignal.any([base, deadline]) : deadline;
  } catch {
    return base ?? null;
  }
}

/** Marks a query as a floor read, so sign-out can find every one of them. */
const FLOOR_READ_META = "floorRead";

/**
 * Sign-out's in-memory half of D-635: drop every floor read from the
 * QueryClient, which outlives the in-app navigation to /login. The storage
 * latch (floor-read-cache.ts) already stops these payloads reaching the
 * device; this stops a floor screen opened under the NEXT session from
 * rendering, or persisting once the cache reopens, a read the previous
 * operator's grants answered.
 */
export function forgetFloorReads(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => query.meta?.[FLOOR_READ_META] === true });
}

export function useCachedRead<T>(options: {
  queryKey: QueryKey;
  cacheKey: string;
  enabled?: boolean;
  /** Receives react-query's abort signal — pass it (via `floorReadSignal`)
   * into the transport, or invalidate cannot cancel an in-flight read and the
   * refetch it owes never fires (the race pinned in
   * use-cached-read.race.test.tsx). */
  queryFn: (ctx: { signal: AbortSignal }) => Promise<T>;
}): FloorRead<T> {
  const online = useOnline();
  const enabled = (options.enabled ?? true) && online;

  const query = useQuery({
    queryKey: options.queryKey,
    enabled,
    retry: false,
    meta: { [FLOOR_READ_META]: true },
    // `networkMode: "always"` would fire doomed requests in a dead zone; the
    // `enabled` gate above already keeps the fetch out of airplane mode, and a
    // request that cannot leave the device is not information.
    queryFn: options.queryFn,
  });

  // CACHED-FIRST. `navigator.onLine` is true whenever the phone is associated
  // with a plant AP — including the dominant plant failure, an AP with no
  // backhaul. The request then hangs in a TCP black hole while a perfectly good
  // snapshot sits on disk, and the operator, looking at a skeleton, concludes
  // the app is broken and walks off. So a snapshot renders IMMEDIATELY
  // (labelled, with its age, and marked as revalidating) and the live answer
  // replaces it when it arrives. The skeleton is only for a screen that has
  // genuinely never loaded this entity.
  //
  // Re-read only when the query settles — a manifest re-renders on every
  // keystroke and a `JSON.parse` of the whole snapshot per keystroke is a real
  // cost on a cheap Android device.
  const cached = useMemo(
    () => loadRead<T>(options.cacheKey),
    [options.cacheKey, query.dataUpdatedAt, query.errorUpdatedAt],
  );

  useEffect(() => {
    if (query.data !== undefined) saveRead(options.cacheKey, query.data);
    // `options.cacheKey` is a primitive; `query.data` identity changes on each
    // successful fetch, which is exactly when the snapshot should be rewritten.
  }, [query.data, options.cacheKey]);

  if (query.data !== undefined) {
    return {
      data: query.data,
      source: "live",
      cachedAt: undefined,
      isLoading: false,
      error: null,
      online,
      revalidating: false,
      refetch: () => void query.refetch(),
    };
  }

  if (cached !== undefined) {
    return {
      data: cached.value,
      source: "cached",
      cachedAt: cached.cachedAt,
      isLoading: false,
      error: query.error,
      online,
      revalidating: enabled && query.isFetching,
      refetch: () => void query.refetch(),
    };
  }

  return {
    data: undefined,
    source: "none",
    cachedAt: undefined,
    isLoading: enabled && query.isLoading,
    error: query.error,
    online,
    revalidating: false,
    refetch: () => void query.refetch(),
  };
}
